import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filter, first, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { NavigationService } from '../../navbar/navigation.service';
import { TenantHelper } from '../../common/helpers/tenant.helper';
import { MetaInfo } from '../../version-control/meta-info.model';
import { PublishDialogComponent } from '../../common/model/publish-changes/publish-dialog-component.model';
import { PublishInventoryContext } from './publish-inventory.context';
import { PublishInventoryChangesetContext } from './publish-inventory-changeset.context';
import { InventoryChangesetItem } from './inventory-changeset-item.model';
import { PublishInventoryInput } from '../inventory.model';

@Component({
    selector: 'adm4-publish-inventory-dialog',
    template: `
    <adm4-modal-dialog-title class='modal-dialog-title full-height-flex'
                             [header]='publishModalName'
                             [showClose]='true'
                             [isFullHeightContent]='true'
                             (closeClicked)="closeDialog(false)">
      <adm4-publish-inventory class='full-height'
                            [changesetItems]='changesetItems$ | async'
                            [initialCommitMessage]='initialCommitMessage$ | async'
                            (publish)='triggerPublishInventory($event)'
                            (cancel)='closeDialog(false)'
                            (isDirty)='onDirtyChanged($event)'></adm4-publish-inventory>
    </adm4-modal-dialog-title>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        PublishInventoryContext,
        PublishInventoryChangesetContext
    ],
    standalone: false
})
export class PublishInventoryDialogComponent implements PublishDialogComponent, OnInit, OnDestroy {
  inventoryKey = inject(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<PublishInventoryDialogComponent>>(MatDialogRef);
  private navigationService = inject(NavigationService);
  private publishInventoryChangesetContext = inject(PublishInventoryChangesetContext);
  private publishInventoryContext = inject(PublishInventoryContext);


  publishModalName: string;
  isDirty = false;
  /** Needed for deactivation guard to find out that dialog can be closed regardless of dirty state */
  explicitExit = false;

  changesetItems$: Observable<InventoryChangesetItem<MetaInfo, any>[]>;
  initialCommitMessage$: Observable<string>;

  private destroyed$: Subject<boolean> = new Subject();

  constructor() {
    this.publishModalName = 'Publish changes ' + TenantHelper.cropTenantFromKey(this.inventoryKey);
    this.changesetItems$ = this.publishInventoryChangesetContext.changesetItems$;
    this.initialCommitMessage$ = this.publishInventoryChangesetContext.initialCommitMessage$;
  }

  ngOnInit() {
    this.dialogRef.keydownEvents().pipe(
      filter((event: KeyboardEvent) => event.key === 'Escape'),
      takeUntil(this.dialogRef.beforeClosed()),
      takeUntil(this.destroyed$))
      .subscribe(() => this.closeDialog(false));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onDirtyChanged(isDirty: boolean): void {
    this.isDirty = isDirty;
  }

  triggerPublishInventory(input: PublishInventoryInput): void {
    this.publishInventoryContext.requestPublishInventoryChanges(input).pipe(first()).subscribe(confirmed => {
      if (confirmed) {
        this.closeDialog(true);
      }
    });
  }

  /**
   * By setting the param true, it can be signalled that the dialog can be closed regardless of the dirty flag.
   * @param explicitExit
   */
  closeDialog(explicitExit: boolean): void {
    this.explicitExit = explicitExit;
    this.navigationService.navigateAwayFromModalWindow();
  }

}
